import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const LiveGameContext = createContext();

export function LiveGameTimerProvider({ children }) {
    const [currentTime, setCurrentTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
    const [isTimerPaused, setIsTimerPaused] = useState(true);
    
    const handleAutoPause = useCallback(async () => {
        setIsTimerPaused(true);
    }, []);

    useEffect(() => {
        let interval = null;
    
        if (!isTimerPaused) {
            interval = setInterval(() => {
                setCurrentTime((prevTime) => {
                    const { hours, minutes, seconds } = prevTime;
                    
                    // Check if time has reached zero
                    if ((hours === 0 && minutes === 0 && seconds === 0) || (hours < 0 || minutes < 0 || seconds < 0)) {
                        clearInterval(interval); // Stop the timer
                        handleAutoPause(); // Trigger the auto-pause logic
                        return prevTime; // Return unchanged time (already 0)
                    }
    
                    // Decrement time
                    let newSeconds = seconds - 1;
                    let newMinutes = minutes;
                    let newHours = hours;
    
                    if (newSeconds < 0) {
                        newSeconds = 59;
                        newMinutes -= 1;
                    }
                    if (newMinutes < 0) {
                        newMinutes = 59;
                        newHours -= 1;
                    }
    
                    return { hours: newHours, minutes: newMinutes, seconds: newSeconds };
                });
            }, 1000);
        } else {
            clearInterval(interval);
        }
    
        return () => clearInterval(interval); // Cleanup on unmount or dependency change
    }, [isTimerPaused, handleAutoPause]);
        
    const toggleTimer = useCallback((pausedState) => setIsTimerPaused(pausedState), []);    
    const updateTimer = useCallback((newTime) => setCurrentTime(newTime), []);

    // Memoize the context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
            currentTime,
            isTimerPaused,
            toggleTimer,
            updateTimer,
        }),
        [currentTime, isTimerPaused, toggleTimer, updateTimer]
    );

    return (<LiveGameContext.Provider value={value}>
        {children}
    </LiveGameContext.Provider>);
}

export function useLiveGameTimerContext() {
    return useContext(LiveGameContext);
}